/**
 * Retry helper for transient network failures during bootstrap.
 *
 * The Chrome `ERR_NETWORK_CHANGED` error (and similar transient failures) is emitted when the OS
 * network stack reports a change mid-request. It is environmental and cannot be prevented, but it
 * is recoverable: retrying the request after a short delay usually succeeds. This helper retries
 * `fn` with exponential backoff while the error looks like a transient network flap, and rethrows
 * immediately for anything else (e.g. an HTTP response with a 4xx/5xx status, which is a real
 * server answer, not a network change).
 */

interface NetworkRetryOptions {
  retries?: number
  baseDelayMs?: number
  maxDelayMs?: number
}

const DEFAULT_RETRIES = 5
const DEFAULT_BASE_DELAY_MS = 500
const DEFAULT_MAX_DELAY_MS = 5000

/** Códigos de abort deliberado. NUNCA são transientes: retentar um abort desfaz o cancelamento. */
const CANCELED_CODES = ['ERR_CANCELED', 'ABORT_ERR']

/** Códigos de falha de transporte potencialmente recuperável. */
const TRANSIENT_CODES = [
  'ERR_NETWORK',
  'ERR_NETWORK_CHANGED',
  'ECONNABORTED',
  'ECONNRESET',
  'ETIMEDOUT',
  'ERR_SOCKET_CONNECTION_TIMEOUT'
]

/** Mensagem de timeout do axios (`timeout of 30000ms exceeded`) — não tem código próprio útil. */
const AXIOS_TIMEOUT_MESSAGE = /timeout of \d+ms exceeded/i

export type HttpFailureKind = 'transient' | 'canceled' | 'server' | 'unknown'

/**
 * Classifica uma falha para decidir quem (se alguém) deve retentar.
 *
 * - `transient`: flap de rede ou timeout — o transporte retenta.
 * - `canceled`: abort deliberado (unmount, AbortController) — ninguém retenta, e não é erro para o
 *   usuário.
 * - `server`: o servidor respondeu (4xx/5xx) — decisão de quem chama, nunca do transporte.
 * - `unknown`: qualquer outra coisa.
 */
export function classifyHttpFailure(error: unknown): HttpFailureKind {
  if (!error || typeof error !== 'object') return 'unknown'

  const candidate = error as { code?: string; message?: string; response?: unknown; name?: string }
  const code = candidate.code

  if (
    (code && CANCELED_CODES.includes(code)) ||
    candidate.name === 'AbortError' ||
    candidate.name === 'CanceledError'
  ) {
    return 'canceled'
  }

  // Resposta do servidor é resposta, não mudança de rede.
  if (candidate.response) return 'server'

  if (code && TRANSIENT_CODES.includes(code)) return 'transient'

  const message = typeof candidate.message === 'string' ? candidate.message.toLowerCase() : ''
  if (
    message.includes('network error') ||
    message.includes('err_network_changed') ||
    message.includes('network changed') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    AXIOS_TIMEOUT_MESSAGE.test(message)
  ) {
    return 'transient'
  }

  return 'unknown'
}

/**
 * Heuristic for "this failure is a transient network flap, retrying might help".
 * Errors that carry a `response` are server answers (4xx/5xx) and are NOT retried.
 */
export function isTransientNetworkError(error: unknown): boolean {
  return classifyHttpFailure(error) === 'transient'
}

/**
 * Waits `delayMs`, but resolves earlier if the browser fires an `online` event — so a recovered
 * connection retries immediately instead of waiting out the full backoff.
 */
function waitBeforeRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false

    const done = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (typeof window !== 'undefined') window.removeEventListener('online', done)
      resolve()
    }

    const timer = setTimeout(done, delayMs)
    if (typeof window !== 'undefined') window.addEventListener('online', done)
  })
}

export async function withNetworkRetry<T>(fn: () => Promise<T>, opts: NetworkRetryOptions = {}): Promise<T> {
  const retries = opts.retries ?? DEFAULT_RETRIES
  const baseDelayMs = opts.baseDelayMs ?? DEFAULT_BASE_DELAY_MS
  const maxDelayMs = opts.maxDelayMs ?? DEFAULT_MAX_DELAY_MS

  let attempt = 0

  for (;;) {
    try {
      return await fn()
    } catch (error) {
      if (attempt >= retries || !isTransientNetworkError(error)) throw error

      // Full jitter: sem isso, N abas/requisições que falharam no mesmo flap retentam todas no
      // mesmo instante e recriam a rajada que causou o problema.
      const ceiling = Math.min(baseDelayMs * 2 ** attempt, maxDelayMs)
      const delay = ceiling * (0.5 + Math.random() * 0.5)
      attempt++
      await waitBeforeRetry(delay)
    }
  }
}
